import axios from 'axios';

const api = axios.create({ withCredentials: true });

let isRefreshing = false;
let failedQueue = [];

// Interceptor-IDs für sauberes Ersetzen bei erneutem Setup-Aufruf
let reqInterceptorId = null;
let resInterceptorId = null;

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) prom.reject(error);
        else prom.resolve(token);
    });
    failedQueue = [];
};

export const setupAxiosInterceptors = (navigate) => {
    // Alte Interceptoren entfernen, damit sie sich nicht stapeln
    if (reqInterceptorId !== null) api.interceptors.request.eject(reqInterceptorId);
    if (resInterceptorId !== null) api.interceptors.response.eject(resInterceptorId);

    reqInterceptorId = api.interceptors.request.use(config => {
        const token = localStorage.getItem('authToken');
        if (token) config.headers['Authorization'] = `Bearer ${token}`;
        const bypass = sessionStorage.getItem('wartungBypass');
        if (bypass) config.headers['x-wartung-bypass'] = bypass;
        return config;
    });

    resInterceptorId = api.interceptors.response.use(
        response => response,
        async error => {
            const originalRequest = error.config;
            const status = error.response?.status;

            // Wartungsmodus: 503 WARTUNG → Event auslösen statt reload (verhindert Endlos-Loop)
            if (status === 503 && error.response?.data?.code === 'WARTUNG') {
                window.dispatchEvent(new CustomEvent('nzapp:wartung', {
                    detail: { message: error.response.data.message }
                }));
                return Promise.reject(error);
            }

            // Lizenzfehler: 402 LICENSE_ERROR → Lizenz-Fehlerseite anzeigen
            if (status === 402 && error.response?.data?.code === 'LICENSE_ERROR') {
                window.dispatchEvent(new CustomEvent('nzapp:lizenzfehler', {
                    detail: {
                        status:  error.response.data.status,
                        message: error.response.data.message,
                    }
                }));
                return Promise.reject(error);
            }

            // Nicht-401 oder bereits retryed → direkt weiterwerfen
            if (status !== 401 || originalRequest._retry) {
                return Promise.reject(error);
            }

            // Refresh-Endpunkt selbst darf keine Retry-Schleife auslösen
            if (originalRequest.url?.includes('/api/refresh-token') || originalRequest.url?.includes('/api/login')) {
                return Promise.reject(error);
            }

            const refreshToken = localStorage.getItem('refreshToken');
            if (!refreshToken) {
                if (navigate) navigate('/login', { state: { message: 'Sitzung abgelaufen. Bitte erneut anmelden.' } });
                else window.location.href = '/login';
                return Promise.reject(error);
            }

            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                }).then(token => {
                    originalRequest.headers['Authorization'] = `Bearer ${token}`;
                    return api(originalRequest);
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                const { data } = await axios.post('/api/refresh-token', { refreshToken });
                const newToken = data.token;
                localStorage.setItem('authToken', newToken);
                api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
                originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                processQueue(null, newToken);
                return api(originalRequest);
            } catch (refreshErr) {
                processQueue(refreshErr, null);
                localStorage.removeItem('authToken');
                localStorage.removeItem('refreshToken');
                if (navigate) navigate('/login', { state: { message: 'Sitzung abgelaufen. Bitte erneut anmelden.' } });
                else window.location.href = '/login';
                return Promise.reject(refreshErr);
            } finally {
                isRefreshing = false;
            }
        }
    );
};

export default api;
